import React from "react";
import { ImportSteps, ValidationRow, SummaryStat } from "../../../../shared/components/ImportSteps";

interface ImportStepsProductCustomerProps {
    currentStep: "file" | "validation" | "confirmation";
    selectedFile: File | null;
    importMode: "flush" | "update" | null;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onImportModeChange: (mode: "flush" | "update") => void;
    onNext: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    isUploading: boolean;
    hasValidationErrors: boolean;
    validatedRows: ValidationRow[];
    onBack: () => void;
    onImport: () => void;
    originalRowCount?: number;
}

export const ImportStepsProductCustomer = (props: ImportStepsProductCustomerProps) => {
    const { originalRowCount, validatedRows, ...restProps } = props;
    
    // Calculate summary stats
    const validRows = validatedRows.filter((r) => r.isValid);
    const invalidRows = validatedRows.filter((r) => !r.isValid);
    const totalRowsProcessed = originalRowCount || validatedRows.length;
    
    // Count all errors
    const totalErrorsCount = validatedRows.reduce((sum, row) => sum + (row.errors?.length || 0), 0);
    
    const summaryStats: SummaryStat[] = [
        {
            value: validRows.length,
            label: "Dòng hợp lệ",
            backgroundColor: "#e8f5e9",
            borderColor: "#4caf50",
            textColor: "#2e7d32",
        },
        {
            value: totalErrorsCount,
            label: "Lỗi",
            backgroundColor: totalErrorsCount > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: totalErrorsCount > 0 ? "#f44336" : "#4caf50",
            textColor: totalErrorsCount > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: invalidRows.length,
            label: "Dòng không hợp lệ",
            backgroundColor: invalidRows.length > 0 ? "#ffcdd2" : "#e8f5e9",
            borderColor: invalidRows.length > 0 ? "#f44336" : "#4caf50",
            textColor: invalidRows.length > 0 ? "#c62828" : "#2e7d32",
        },
        {
            value: totalRowsProcessed,
            label: "Tổng số dòng",
            backgroundColor: "#e3f2fd",
            borderColor: "#2196f3",
            textColor: "#1565c0",
        },
    ];
    
    const fileFormatInstructions = (
        <>
            <div style={{ fontWeight: 500, marginBottom: "4px" }}>Lưu ý về định dạng file:</div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li>Dòng 1: Headers tiếng Việt (có thể merge cells)</li>
                <li>Dòng 2: Headers tiếng Anh (optional)</li>
                <li>Dòng 3: Tên trường database (product_code, product_name, base_product_code, customer_code, customer_name, addon_name, addon_quantity, price, quantity, note)</li>
                <li>Dòng 4+: Dữ liệu (mỗi dòng = một quan hệ sản phẩm-khách hàng-dịch vụ kèm theo)</li>
                <li>Chỉ sử dụng sheet đầu tiên</li>
                <li>
                    <strong>Trường bắt buộc:</strong> product_code, product_name, base_product_code, customer_code
                </li>
                <li>
                    <strong>Trường tùy chọn:</strong> customer_name (dùng để tra cứu nếu customer_code không tìm thấy), addon_name, addon_quantity, price (giá bán - nếu để trống sẽ dùng giá từ sản phẩm gốc), quantity (số lượng - nếu để trống sẽ là 0), note (ghi chú)
                </li>
                <li>
                    <strong>Lưu ý:</strong> Nếu có addon_name thì phải có addon_quantity và ngược lại
                </li>
                <li>
                    <strong>Quan trọng:</strong> Mã sản phẩm và tên sản phẩm phải là duy nhất trong hệ thống
                </li>
                <li>
                    <strong>Quan trọng:</strong> Đối với một nhóm sản phẩm-khách hàng có nhiều dịch vụ kèm theo (nhiều dòng), tất cả các ô trong nhóm phải được điền đầy đủ (product_code, product_name, base_product_code, customer_code phải giống nhau ở tất cả các dòng trong nhóm)
                </li>
                <li>Sản phẩm gốc (base_product_code) phải tồn tại trong hệ thống</li>
                <li>Khách hàng (customer_code hoặc customer_name) phải tồn tại trong hệ thống</li>
                <li>Dịch vụ kèm theo (addon_name) là tùy chọn - nếu có thì phải tồn tại trong hệ thống</li>
                <li>Cùng một sản phẩm-khách hàng có thể có nhiều dịch vụ kèm theo (mỗi dịch vụ một dòng)</li>
                <li>
                    <strong>Định dạng đề xuất:</strong> Mã sản phẩm = "MÃ_GỐC-MÃ_KH", Tên sản phẩm = "Tên_Gốc-Tên_KH"
                </li>
                <li>Nếu có số lượng dịch vụ kèm theo (addon_quantity) thì phải lớn hơn 0</li>
            </ul>
        </>
    );

    return (
        <ImportSteps
            {...restProps}
            validatedRows={validatedRows}
            title="Nhập quan hệ sản phẩm-khách hàng"
            summaryStats={summaryStats}
            templateUrl="/templates/product_customer_import.xlsx"
            importModeLabels={{
                update: {
                    title: "Cập nhật và thêm mới",
                    description: "Quan hệ sản phẩm-khách hàng mới sẽ được thêm vào. Quan hệ trùng sẽ được cập nhật.",
                },
                flush: {
                    title: "Xóa tất cả và nhập mới",
                    description: "Tất cả quan hệ sản phẩm-khách hàng hiện có sẽ bị xóa vĩnh viễn và thay thế bằng dữ liệu từ file. Hành động này không thể hoàn tác.",
                    warning: "CẢNH BÁO",
                },
            }}
            fileFormatInstructions={fileFormatInstructions}
            previewColumns={[
                { key: "product_code", label: "Mã sản phẩm" },
                { key: "product_name", label: "Tên sản phẩm" },
                { key: "base_product_code", label: "Mã sản phẩm gốc" },
                { key: "customer_code", label: "Mã khách hàng" },
                { key: "customer_name", label: "Tên khách hàng" },
                { key: "addon_name", label: "Tên dịch vụ kèm theo" },
                { key: "addon_quantity", label: "Số lượng dịch vụ kèm theo" },
                { key: "price", label: "Giá bán" },
                { key: "quantity", label: "Số lượng" },
                { key: "note", label: "Ghi chú" },
            ]}
        />
    );
};

